import type {
  Valhalla_CostingModel,
  Valhalla_Trip,
} from "@/api/tripPlanning/types/valhallaTypes";
import type { MapGradientPoint } from "@/stores/mapStore";
import {
  ItineraryCore,
  type ItineraryCoreOptions,
} from "../core/itineraryCoreClass";
import type { TripLocationCore } from "../core/tripLocationCoreClass";
import type { ItineraryDestinationICE, ItineraryStepICE } from "../types";

type ItineraryICEOptions = {
  costingModel?: Valhalla_CostingModel | null;
};

export class ItineraryICE extends ItineraryCore {
  // ----------------------------------------------------------------------- //
  // -------------------------------- State -------------------------------- //
  // ----------------------------------------------------------------------- //

  /** The valhalla costing model used in the source route plan to create this itinerary. */
  costingModel: Valhalla_CostingModel | null;

  // ----------------------------------------------------------------------- //
  // ----------------------------- Constructor ----------------------------- //
  // ----------------------------------------------------------------------- //

  constructor(options: ItineraryICEOptions & ItineraryCoreOptions = {}) {
    super(options);
    this.costingModel = options.costingModel ?? null;
  }

  /**
   * Builds an ICE itinerary from a Valhalla trip response.
   *
   * @param trip The Valhalla trip response.
   * @param locations The trip locations.
   * @param startingLoad The starting vehicle load in kg.
   * @returns A populated ICE itinerary.
   */
  public static buildFromValhallaTrip({
    trip,
    locations,
    startingLoad,
    costingModel,
  }: {
    trip: Valhalla_Trip;
    locations: TripLocationCore[];
    startingLoad: number;
    costingModel: Valhalla_CostingModel;
  }): ItineraryICE {
    const steps: ItineraryStepICE[] = [];
    const lastLocation = locations[locations.length - 1];

    const destination: ItineraryDestinationICE | undefined = lastLocation
      ? {
          addressStr: lastLocation.address,
          name: lastLocation.name ?? "Destination",
        }
      : undefined;

    let remainingLoad = startingLoad;

    trip.legs.forEach((leg, index) => {
      const originalLocationIndex = trip.locations[index]!.original_index;

      const location =
        originalLocationIndex >= 0
          ? originalLocationIndex > locations.length - 1
            ? undefined
            : locations[originalLocationIndex]
          : undefined;

      if (!location) {
        throw new Error(
          `Starting location relating to leg ${index} not found.`,
        );
      }

      const departureLoadWeight = Math.max(
        remainingLoad + (location.weightChange ?? 0),
        0,
      );

      const ferryTime = leg.summary.has_ferry
        ? leg.maneuvers.reduce(
            (acc, manoeuvre) => acc + (manoeuvre.ferry ? manoeuvre.time : 0),
            0,
          )
        : 0;

      const drivingTime = leg.summary.has_ferry
        ? leg.maneuvers.reduce(
            (acc, manoeuvre) => acc + (!manoeuvre.ferry ? manoeuvre.time : 0),
            0,
          )
        : leg.summary.time;

      steps.push({
        addressStr: location.address,
        name: location.name ?? "Unnamed location",
        polyline: leg.shape,

        drivingDistance: leg.summary.length * 1000,
        drivingTime,
        ferryTime,

        locationStayTime: location.stay ?? 0,

        arrivalLoadWeight: remainingLoad,
        departureLoadWeight,
        userAdded: true,
      });

      remainingLoad = departureLoadWeight;
    });

    return new ItineraryICE({
      steps,
      destination,
      costingModel,
    });
  }


  public static fromDTO(dto: unknown): ItineraryICE {
  if (!isItineraryICEDTO(dto)) {
    throw new Error("Invalid ItineraryICE DTO");
  }

  return new ItineraryICE({
    steps: dto.steps,
    destination: dto.destination,
    costingModel: dto.costingModel,
  });
}

  // ----------------------------------------------------------------------- //
  // -------------------------------- Getters ------------------------------ //
  // ----------------------------------------------------------------------- //

  /**
   * Returns the total time spent at scheduled stops for all steps in the
   * itinerary.
   *
   * @return {number} The total stopped time in seconds.
   */
  public get totalStoppedTime(): number {
    return this.steps.reduce((acc, step) => acc + step.locationStayTime, 0);
  }

  // ----------------------------------------------------------------------- //
  // -------------------------------- Methods ------------------------------ //
  // ----------------------------------------------------------------------- //

  /**
   * Persisted shape of this itinerary, for storing in a route plan's
   * `itinerary` column. Mirrors what `fromDTO` above expects back.
   */
  public compileDTO(): ItineraryICEDTO {
    return {
      steps: this.steps,
      destination: this.destination ?? undefined,
      costingModel: this.costingModel,
    };
  }

  /**
   * Gets map gradient points grouped by leg.
   *
   * Adds a default intensity value of 1 for ICE trips where no energy
   * consumption data exists.
   *
   * @returns Gradient points grouped by itinerary leg.
   */
  public getGradientPointsByLeg(): MapGradientPoint[][] {
    return this.pointsByLeg.map((legPoints) =>
      legPoints.map(([lat, lng]) => [lat, lng, 1]),
    );
  }
}


type ItineraryICEDTO = {
  steps: ItineraryStepICE[];
  destination?: ItineraryDestinationICE;
  costingModel?: Valhalla_CostingModel | null;
};

function isItineraryICEDTO(value: unknown): value is ItineraryICEDTO {
  if (!value || typeof value !== "object") {
    return false;
  }

  const dto = value as Record<string, unknown>;

  return (
    Array.isArray(dto.steps) &&
    (dto.destination === undefined ||
      (typeof dto.destination === "object" && dto.destination !== null)) &&
    (dto.costingModel === undefined ||
      dto.costingModel === null ||
      typeof dto.costingModel === "string")
  );
}